<template>
  <sc-render :fields="fields" :empty-message="$t('NO_DATA_SOURCE_EXIST')">
    <div
      class="container"
      :class="[themeClass, imagePositionClass]"
    >
      <div class="content-wrapper">
        <component
          :is="componentType"
          :field="!isEditMode && ctaField"
          v-bind="componentAttrs"
          class="image-wrapper"
        >
          <media-display
            :sc-image-field="imageField"
            :image-desktop-source="imageSrc"
            :fraction="fraction"
          />
        </component>
        <article class="text-block" :style="mobileDescriptionStyle">
          <component
            :is="componentType"
            :field="!isEditMode && ctaField"
            v-bind="componentAttrs"
          >
            <heading
              v-if="showTitle"
              class="title"
              desktopSize="size-3"
              mobileSize="size-5"
              :tagField="headingSizeField"
              :textField="titleField"
            />
            <sc-text
              v-if="showDescription"
              class="description"
              tag="p"
              :field="descriptionField"
            />
            <sc-text
              v-if="showTag"
              class="tag"
              tag="p"
              :field="tagField"
            />
          </component>
          <sh-button
            v-if="showCta"
            class="cta"
            :[btnLinkKey]="ctaHref"
            :color="buttonColor"
            :[setStyle]="true"
            :aria-label="ctaHiddenText || ctaTitle"
          >
            {{ ctaText }}
          </sh-button>
        </article>
      </div>
    </div>
  </sc-render>
</template>
<script>
  import { Image, Link, Text } from '@sitecore-jss/sitecore-jss-vue'
  import { createNamespacedHelpers } from 'vuex'
  import mapFields from '~/helpers/map-fields'
  import { getButtonType } from '~/helpers/get-button-type'
  import CTA from '~/constants/appearance'
  import getButtonStyle from '~/helpers/get-button-style'
  import MediaDisplay from '~/components/sitecore/media-display'
  import ShLink from '~/components/sh-link'
  import ScRender from '../sc-render'
  import Heading from '../heading'
  import fraction from '~/constants/fraction'
  import ThemeMixin from '~/mixins/theme'

  const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

  export default {
    name: 'EditorialLandscapeImage',

    props: {
      fields: {
        type: Object,
        default: () => ({})
      }
    },

    components: {
      ScImage: Image,
      ScLink: Link,
      ScText: Text,
      ScRender,
      Heading,
      MediaDisplay,
      ShLink
    },

    mixins: [ThemeMixin],

    computed: {
      ...mapJssGetters(['isEditMode']),
      ...mapFields({
        imageField: 'image',
        imageSrc: 'image.value.src',
        imagePosition: 'imagePosition.value',
        titleField: 'title',
        headingSizeField: 'headingSize',
        descriptionField: 'description',
        tagField: 'tag',
        tagText: 'tag.value',
        ctaField: 'cta.value',
        ctaText: 'cta.value.text',
        ctaHref: 'cta.value.href',
        ctaTitle: 'cta.value.title',
        ctaHiddenText: 'cta.value.data-hidden-text',
        hideDescriptionForMobile: 'hideDescriptionForMobile.value',
        ctaAppearance: 'ctaAppearance.value'
      }),
      btnLinkKey () {
        return getButtonType(this.ctaHref)
      },
      buttonColor () {
        return this.isDarkTheme ? CTA.SECONDARY : CTA.PRIMARY
      },
      setStyle () {
        return getButtonStyle(this.ctaAppearance)
      },
      themeClass () {
        return `theme-${this.theme.color}`.toLowerCase()
      },
      componentType () {
        if (!this.isEditMode && this.ctaHref) {
          return 'sh-link'
        }
        return 'div'
      },
      componentAttrs () {
        if (this.componentType === 'sh-link') {
          return {
            to: this.ctaHref
          }
        }

        return {}
      },
      imagePositionClass () {
        return `image-${this.imagePosition || 'left'}`.toLowerCase()
      },
      showTitle () {
        return this.isEditMode || this.titleField
      },
      showDescription () {
        return this.isEditMode || this.descriptionField
      },
      showTag () {
        return this.isEditMode || !!this.tagText
      },
      showCta () {
        return this.ctaAppearance && this.ctaHref
      },
      mobileDescriptionStyle () {
        const displayValue = this.hideDescriptionForMobile ? 'none' : 'block'
        return {
          // the CSS variable is set in accordance with the hidden description setting
          '--display-description': displayValue
        }
      },
      fraction () {
        return fraction.threeFr // see styles for width
      }
    }
  }
</script>
<style lang="scss" scoped>
  .container {
    @include stack-space($space-090);

    @include tablet {
      @include stack-space(0);

      .image-wrapper {
        padding-left: $space-060;
        padding-right: $space-080;
      }

      .text-block {
        @include reset;
        padding-right: $space-060;
      }

      &.image-right {
        .content-wrapper {
          flex-direction: row-reverse;
        }

        .text-block {
          @include reset;
          padding-left: $space-060;
        }

        .image-wrapper {
          padding-right: $space-060;
          padding-left: $space-080;
        }
      }
    }
  }

  .content-wrapper {
    display: flex;
    flex-direction: column;
    text-decoration: none;

    @include tablet {
      flex-direction: row;
      padding: $space-100 0;
    }
  }

  .text-block {
    @include inset-space($space-050);
    padding-bottom: 0;
    text-decoration: none;

    @include tablet {
      flex: 0 0 70%;
      @include reset;
      padding-right: $space-080;
    }

    a {
      text-decoration: none;
    }
  }

  .title {
    padding: 0;
    @include stack-space($space-030);

    @include tablet {
      @include stack-space($space-050);
    }
  }

  .description {
    display: var(--display-description);
    @include font('040');
    @include stack-space($space-030);

    @include tablet {
      display: block;
      @include font('070');
      @include stack-space($space-050);
    }
  }

  .tag {
    @include reset;
    @include font('030');
    text-transform: uppercase;

    @include tablet {
      @include font('040');
    }
  }

  .cta {
    margin-top: $space-050;

    @include tablet {
      margin-top: $space-060;
    }
  }

  .image-wrapper {
    display: block;

    @include tablet {
      flex: 0 0 30%; //see computed for fraction
    }
  }

  .theme-dark {
    .title,
    .description,
    .tag {
      color: $color-white;
    }

    a:focus {
      outline-color: $color-white;
    }
  }
</style>
